import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import {
  normalizeChecklistItems,
  type IsoCriterionResult,
  type NormalizeChecklistOptions,
} from "./checklist";

/** Nhóm SP trên form FQC -> phần mã của mẫu FQC tương ứng (TPL-FQC-FOLDING-BOX...) */
const FQC_CATEGORY_TEMPLATE_KEY: Record<string, string> = {
  HOP_MEM: "FOLDING",
  HOP_CUNG: "RIGID",
  THUNG_CARTON: "CORRUGATED",
  TUI_GIAY: "PAPER-BAG",
};

export function templateCriteriaOf(tpl: any): any[] {
  if (Array.isArray(tpl?.criteria)) return tpl.criteria;
  if (Array.isArray(tpl?.schema?.fields)) return tpl.schema.fields;
  return [];
}

interface Options {
  qcType: "IQC" | "IPQC" | "FQC";
  materialCategory?: string | null;
  processStage?: string | null;
  productTypeId?: string | null;
  productTypeCode?: string | null;
  templateId?: string | null;
  checklistItems: IsoCriterionResult[];
  onTemplateChange: (templateId: string | null, templateObj?: any) => void;
  onChecklistChange: (items: IsoCriterionResult[]) => void;
  normalizeOptions?: NormalizeChecklistOptions;
  /** Không tự chọn / nạp mẫu (phiếu chỉ xem) */
  disabled?: boolean;
}

/**
 * Danh sách mẫu kiểm tra theo phân hệ + tự chọn mẫu phù hợp (nhóm vật tư / công đoạn / loại SP)
 * và nạp chỉ tiêu khi phiếu chưa có chỉ tiêu.
 */
export function useQcTemplateCriteria({
  qcType,
  materialCategory,
  processStage,
  productTypeId,
  productTypeCode,
  templateId,
  checklistItems,
  onTemplateChange,
  onChecklistChange,
  normalizeOptions,
  disabled,
}: Options) {
  const { data: templatesData } = useQuery(
    trpc.qcTemplates.list.queryOptions({ page: 1, limit: 100, type: qcType } as any),
  );
  const allTemplates: any[] = useMemo(() => (templatesData as any)?.items || [], [templatesData]);
  // Chỉ mẫu Đang áp dụng (bản Lưu trữ / Nháp không chọn cho phiếu mới); phiếu cũ vẫn thấy mẫu của mình
  const templates: any[] = useMemo(
    () =>
      allTemplates.filter(
        (t: any) => !t.status || t.status === "ACTIVE" || (templateId && (t.id === templateId || t.code === templateId)),
      ),
    [allTemplates, templateId],
  );

  useEffect(() => {
    if (disabled || !templates.length) return;

    if (templateId) {
      const current = allTemplates.find((t: any) => t.id === templateId || t.code === templateId);
      if (current && checklistItems.length === 0) {
        onChecklistChange(normalizeChecklistItems(templateCriteriaOf(current), [], normalizeOptions));
      }
      return;
    }

    let matched: any = null;
    const active = templates.filter((t: any) => !t.status || t.status === "ACTIVE");
    if (qcType === "IQC" && materialCategory) {
      const target = materialCategory.toUpperCase();
      matched = active.find(
        (t: any) =>
          (t.materialCategory || t.materialType)?.toUpperCase() === target || t.code?.toUpperCase().includes(target),
      );
    } else if (qcType === "IPQC" && processStage) {
      matched = active.find(
        (t: any) =>
          t.processStage?.toUpperCase() === processStage.toUpperCase() ||
          t.code?.toUpperCase().includes(processStage.toUpperCase()),
      );
    } else if (qcType === "FQC" && (productTypeId || productTypeCode)) {
      const codeKey = productTypeCode ? FQC_CATEGORY_TEMPLATE_KEY[productTypeCode.toUpperCase()] || productTypeCode.toUpperCase() : "";
      // Ưu tiên: đúng loại sản phẩm → đúng nhóm thành phẩm của mẫu (danh mục Cấu hình) → mã mẫu cũ theo nhóm
      matched =
        (productTypeId && active.find((t: any) => t.productTypeId === productTypeId)) ||
        (productTypeCode && active.find((t: any) => t.productCategory?.toUpperCase() === productTypeCode.toUpperCase() && !t.productTypeId)) ||
        (codeKey && active.find((t: any) => t.code?.toUpperCase().includes(codeKey))) ||
        null;
    }
    if (!matched) {
      // IPQC: mẫu của công đoạn khác không dùng thay (Đóng gói từng nạp mẫu In Offset) — chỉ mẫu "Mọi công đoạn"
      const pool = qcType === "IPQC" ? active.filter((t: any) => !t.processStage) : active;
      matched = pool.find((t: any) => t.isDefault) || pool[0];
    }

    if (matched) {
      onTemplateChange(matched.id, matched);
      if (checklistItems.length === 0) {
        onChecklistChange(normalizeChecklistItems(templateCriteriaOf(matched), [], normalizeOptions));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, qcType, materialCategory, processStage, productTypeId, productTypeCode, templateId, disabled]);

  const selectTemplate = (nextId: string) => {
    const found = allTemplates.find((t: any) => t.id === nextId);
    onTemplateChange(nextId, found);
    if (found) onChecklistChange(normalizeChecklistItems(templateCriteriaOf(found), [], normalizeOptions));
  };

  return { templates, selectTemplate };
}
